import React, { useCallback, useMemo, useRef, useState } from 'react';
import PayrollForm from './PayrollForm';
import { payrollService } from '../services/payrollService';

const defaultPeriod = () => ({
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
});

const PayrollBatchForm = ({ employeeId, employeeName, employees = [], onSave, onCancel }) => {
  const [period, setPeriod] = useState(defaultPeriod());
  const [forms, setForms] = useState([{ id: crypto.randomUUID?.() || String(Date.now()) }]);
  const [saving, setSaving] = useState(false);
  const [batchError, setBatchError] = useState('');

  const refs = useRef([]);
  if (refs.current.length !== forms.length) {
    refs.current = forms.map((_, i) => refs.current[i] || React.createRef());
  }

  const disableOthersPeriod = true;

  const handlePeriodChange = useCallback(({ year, month }) => {
    const y = year !== '' && year != null ? parseInt(year, 10) : '';
    const m = month !== '' && month != null ? parseInt(month, 10) : '';
    setPeriod((prev) => ({
      year: Number.isFinite(y) ? y : prev.year,
      month: Number.isFinite(m) ? m : prev.month,
    }));
  }, []);

  const canAdd = useMemo(() => !saving, [saving]);

  const addAnother = useCallback(() => {
    if (!canAdd) return;
    setForms((prev) => [...prev, { id: crypto.randomUUID?.() || String(Date.now() + prev.length) }]);
  }, [canAdd]);

  const removeAt = useCallback((idx) => {
    setForms((prev) => prev.filter((_, i) => i !== idx));
    refs.current = refs.current.filter((_, i) => i !== idx);
  }, []);

  const saveAll = useCallback(async () => {
    setBatchError('');
    setSaving(true);
    try {
      const payloads = [];
      for (let i = 0; i < refs.current.length; i++) {
        const r = refs.current[i]?.current;
        if (!r) continue;
        const ok = r.validate();
        if (!ok) {
          setBatchError(`Veuillez corriger les erreurs dans la fiche ${i + 1}.`);
          setSaving(false);
          return;
        }
        payloads.push(r.getPayrollData());
      }

      // Création séquentielle (plus simple à diagnostiquer si une fiche échoue)
      for (let i = 0; i < payloads.length; i++) {
        await payrollService.create(payloads[i]);
      }
      onSave();
    } catch (err) {
      setBatchError(err.response?.data?.error || 'Erreur lors de l’enregistrement des fiches');
    } finally {
      setSaving(false);
    }
  }, [onSave]);

  return (
    <div className="payroll-batch">
      {forms.map((f, idx) => (
        <div key={f.id} className="payroll-batch-item">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontWeight: 600 }}>Fiche {idx + 1}</div>
            {forms.length > 1 && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => removeAt(idx)}
                disabled={saving}
              >
                Retirer
              </button>
            )}
          </div>

          <PayrollForm
            ref={refs.current[idx]}
            employeeId={idx === 0 ? employeeId : null}
            employeeName={idx === 0 ? employeeName : null}
            employees={employees}
            onSave={() => {}}
            onCancel={() => {}}
            periodYear={period.year}
            periodMonth={period.month}
            disablePeriodEdit={disableOthersPeriod && idx !== 0}
            onPeriodChange={idx === 0 ? handlePeriodChange : null}
            batchMode
            titlePrefix={`Fiche ${idx + 1}`}
          />

          {idx === 0 && (
            <div style={{ marginTop: '-10px', marginBottom: '10px', fontSize: '0.9em', color: '#666' }}>
              Changer l’année / mois ici appliquera la même période aux autres fiches.
            </div>
          )}
        </div>
      ))}

      {batchError && (
        <div className="error-banner" style={{ marginTop: '1rem' }}>
          {batchError}
        </div>
      )}

      <div className="form-actions" style={{ marginTop: '1rem' }}>
        <button type="button" className="btn btn-outline" onClick={addAnother} disabled={!canAdd}>
          + Ajouter une fiche (même période)
        </button>
        <button type="button" className="btn btn-primary" onClick={saveAll} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer toutes les fiches'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Annuler
        </button>
      </div>
    </div>
  );
};

export default PayrollBatchForm;

